/**
 * FirestoreClient — Abstract interface for Firestore operations.
 *
 * Production code uses {@link FirebaseFirestoreClient} (wraps the Firebase SDK).
 * Unit tests use {@link InMemoryFirestoreClient} (fast, no mocking needed).
 *
 * Services call {@link getFirestoreClient} to obtain the active client.
 * The client is set once at app startup ({@link setFirestoreClient}) and
 * swapped to the in-memory fake in test setup.
 */

// ---------------------------------------------------------------------------
// Snapshot / query result types
// ---------------------------------------------------------------------------

/** Thin read-only view of a Firestore document snapshot. */
export interface FirestoreDocSnapshot {
  readonly id: string;
  exists(): boolean;
  data(): Record<string, unknown> | undefined;
}

/** Thin read-only view of a Firestore query result. */
export interface FirestoreQueryResult {
  readonly docs: FirestoreDocSnapshot[];
  readonly empty: boolean;
}

// ---------------------------------------------------------------------------
// Timestamp
// ---------------------------------------------------------------------------

/** Minimal timestamp contract that mirrors Firestore Timestamp. */
export interface FirestoreTimestamp {
  toDate(): Date;
  readonly seconds: number;
  readonly nanoseconds: number;
}

// ---------------------------------------------------------------------------
// Query constraints
// ---------------------------------------------------------------------------

export interface OrderByConstraint {
  readonly type: "orderBy";
  readonly field: string;
  readonly direction: "asc" | "desc";
}

export type WhereOp =
  | "<"
  | "<="
  | "=="
  | "!="
  | ">="
  | ">"
  | "array-contains"
  | "in"
  | "not-in"
  | "array-contains-any";

export interface WhereConstraint {
  readonly type: "where";
  readonly field: string;
  readonly operator: WhereOp;
  readonly value: unknown;
}

export type FirestoreConstraint = OrderByConstraint | WhereConstraint;

// ---------------------------------------------------------------------------
// Sentinel for serverTimestamp()
// ---------------------------------------------------------------------------

/**
 * Sentinel value returned by {@link FirestoreClient.serverTimestamp}.
 * The in-memory client resolves this to a real timestamp on write.
 */
export const SERVER_TIMESTAMP_SENTINEL = Symbol.for(
  "firestoreClient.serverTimestamp"
);

// ---------------------------------------------------------------------------
// FirestoreClient interface
// ---------------------------------------------------------------------------

export interface FirestoreClient {
  // ---- Document CRUD ----

  /** Read a single document by path segments (e.g. ["users", id]). */
  getDocument(path: string[]): Promise<FirestoreDocSnapshot>;

  /** Create or overwrite a document. Use `{ merge: true }` for partial writes. */
  setDocument(
    path: string[],
    data: Record<string, unknown>,
    options?: { merge?: boolean }
  ): Promise<void>;

  /** Update specific fields of an existing document. */
  updateDocument(path: string[], data: Record<string, unknown>): Promise<void>;

  /** Delete a document. */
  deleteDocument(path: string[]): Promise<void>;

  // ---- Collection queries ----

  /** Query a collection with optional ordering / filtering constraints. */
  queryCollection(
    collectionPath: string[],
    constraints?: FirestoreConstraint[]
  ): Promise<FirestoreQueryResult>;

  // ---- Real-time listeners ----

  /** Subscribe to document changes. Returns an unsubscribe function. */
  onDocumentSnapshot(
    path: string[],
    onNext: (snapshot: FirestoreDocSnapshot) => void,
    onError?: (error: Error) => void
  ): () => void;

  /** Subscribe to collection changes. Returns an unsubscribe function. */
  onCollectionSnapshot(
    collectionPath: string[],
    constraints: FirestoreConstraint[],
    onNext: (result: FirestoreQueryResult) => void,
    onError?: (error: Error) => void
  ): () => void;

  // ---- ID generation ----

  /** Generate a unique document ID within a collection. */
  generateId(collectionPath: string[]): string;

  // ---- Timestamps ----

  /**
   * Returns a sentinel that resolves to the server time on write.
   * Cast the return value to `unknown` — services pass it as a field value.
   */
  serverTimestamp(): unknown;

  /** Convert a JS Date to a Firestore-compatible timestamp object. */
  timestampFromDate(date: Date): FirestoreTimestamp;

  // ---- Lifecycle ----
}

// ---------------------------------------------------------------------------
// Module-level singleton + getter / setter
// ---------------------------------------------------------------------------

let activeClient: FirestoreClient | null = null;

/**
 * Return the active FirestoreClient.
 * @throws if no client has been set yet.
 */
export function getFirestoreClient(): FirestoreClient {
  if (!activeClient) {
    throw new Error(
      "FirestoreClient not initialised. " +
        "Call setFirestoreClient() during app startup or test setup."
    );
  }
  return activeClient;
}

/** Install a FirestoreClient (production or test). */
export function setFirestoreClient(client: FirestoreClient): void {
  activeClient = client;
}

/** Clear the active client (useful in afterEach). */
export function resetFirestoreClient(): void {
  activeClient = null;
}
